import { useEffect, useState } from "react";
import NumberQuestion from "../../FormInputElements/NumberQuestion";
import SelectMachineField from "./SelectMachineField";
import { useSearchParams } from "react-router-dom";
import OperationDetailsFields from "./OperationDetailsFields";

const MAX_CULTIVATION_OPERATIONS = 4;
const EMPTY_OPERATION = {
  machine_id: "",
  hours_used: "",
  operation_date: "",
  fuel_used_litres: "",
};

function hasOperationValue(operation = {}) {
  return (
    Boolean(operation.machine_id) ||
    Number(operation.hours_used) > 0 ||
    Boolean(operation.operation_date) ||
    Number(operation.fuel_used_litres) > 0
  );
}

function getVisibleOperationCount(operations = []) {
  const lastActiveIndex = operations.reduce(
    (lastIndex, operation, index) =>
      hasOperationValue(operation) ? index : lastIndex,
    -1
  );

  return Math.min(lastActiveIndex + 1, MAX_CULTIVATION_OPERATIONS);
}

function normalizeOperations(operations = [], visibleCount = MAX_CULTIVATION_OPERATIONS) {
  return Array.from({ length: MAX_CULTIVATION_OPERATIONS }, (_, index) => {
    if (index >= visibleCount) {
      return { ...EMPTY_OPERATION };
    }

    return {
      ...EMPTY_OPERATION,
      ...(operations[index] || {}),
    };
  });
}

export default function CultivationsForm({
  cropYear,
  setCropYear,
  farm,
  setFarm,
}) {
  const [searchParams] = useSearchParams();
  const [numberOfCultivation, setNumberOfCultivation] = useState(
    getVisibleOperationCount(cropYear.cultivations)
  );
  const issueSection = searchParams.get("issueSection");
  const issueField = searchParams.get("issueField");
  const issueRow = Number.parseInt(searchParams.get("issueRow") || "-1", 10);
  const isCultivationIssue =
    issueSection === "cultivations" ||
    (searchParams.get("issuePath") || "").startsWith("cultivations[");

  useEffect(() => {
    if (!isCultivationIssue) {
      return;
    }

    const target = document.getElementById(
      `cultivation-row-${Number.isNaN(issueRow) ? 0 : issueRow}`
    );
    const section = document.getElementById("cropyear-cultivations-section");

    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isCultivationIssue, issueRow]);

  useEffect(() => {
    setNumberOfCultivation(getVisibleOperationCount(cropYear.cultivations));
  }, [cropYear.id]);

  function handleNumberCultivationChange(amount) {
    const nextAmount = Math.max(
      0,
      Math.min(MAX_CULTIVATION_OPERATIONS, Number(amount) || 0)
    );

    setNumberOfCultivation(nextAmount);
    setCropYear((prevState) => ({
      ...prevState,
      cultivations: normalizeOperations(prevState.cultivations, nextAmount),
    }));
  }

  function handleMachineChange(machineId, index) {
    setCropYear((prevState) => {
      const nextCultivations = normalizeOperations(prevState.cultivations);
      nextCultivations[index] = {
        ...nextCultivations[index],
        machine_id: machineId,
      };

      return {
        ...prevState,
        cultivations: nextCultivations,
      };
    });
  }

  function handleHoursChange(hoursUsed, index) {
    setCropYear((prevState) => {
      const nextCultivations = normalizeOperations(prevState.cultivations);
      nextCultivations[index] = {
        ...nextCultivations[index],
        hours_used: hoursUsed,
      };

      return {
        ...prevState,
        cultivations: nextCultivations,
      };
    });
  }

  function handleOperationDetailChange(field, value, index) {
    setCropYear((prevState) => {
      const nextCultivations = normalizeOperations(prevState.cultivations);
      nextCultivations[index] = { ...nextCultivations[index], [field]: value };
      return { ...prevState, cultivations: nextCultivations };
    });
  }

  return (
    <section
      id="cropyear-cultivations-section"
      className="border-b border-slate-100 pb-5"
    >
      <h4 className="mb-3 text-[20px] font-semibold text-slate-700">
        Cultivation
      </h4>
      <NumberQuestion
        min={0}
        max={4}
        fieldValue={numberOfCultivation}
        questionText={
          "How many cultivation passes were completed this crop year?"
        }
        modalTitle={"Number of Cultivation Operations"}
        modalDescription={
          "Please provide the number of cultivation operations you had in this year for this specific field."
        }
        onChange={handleNumberCultivationChange}
      />

      <ul className="transition-all duration-500">
        {normalizeOperations(cropYear.cultivations)
          .slice(0, numberOfCultivation)
          .map((cultivation, index) => (
          <li
            className="border-b border-slate-100 py-2 last:border-b-0"
            id={`cultivation-row-${index}`}
            key={index}
          >
            <SelectMachineField
            fieldLabel={"Cultivation #" + (index + 1) + " equipment"}
            machineType={"TRACTOR"}
            index={index}
            fieldState={cultivation}
            farm={farm}
            setFarm={setFarm}
            handleHoursChange={(hoursUsed) =>
              handleHoursChange(hoursUsed, index)
            }
            handleMachineChange={(machineId) =>
              handleMachineChange(machineId, index)
            }
            errorFound={{
              machineObj:
                isCultivationIssue &&
                issueRow === index &&
                (!issueField || issueField === "machine_id"),
              hoursUsed:
                isCultivationIssue &&
                issueRow === index &&
                (!issueField || issueField === "hours_used"),
            }}
            />
            <OperationDetailsFields
              cropYear={cropYear.crop_year}
              operation={cultivation}
              onChange={(field, value) =>
                handleOperationDetailChange(field, value, index)
              }
              dateLabel={`Cultivation #${index + 1} Date`}
            />
          </li>
        ))}
      </ul>
    </section>
  );
}
